import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { switchMap } from 'rxjs';

import { EventResultFieldDef, EventStanding } from './models';
import { EventService } from './event.service';
import {
    RANK_SORT,
    RECORD_SORT,
    SortDirection,
    StandingsSortKey,
    fieldSortKey,
    sortStandings,
    standingsSortOptions
} from './standings-sort';

/** One metric as rendered on a row: a label, a value, and whether it is what we sorted by. */
interface RowMetric {
    key: string;
    label: string;
    value: string;
}

interface StandingsRow {
    rank: number;
    displayName: string;
    metrics: RowMetric[];
}

@Component({
    standalone: false,
    selector: 'abs-event-standings',
    template: `
        <abs-page-layout [pageTitle]="'GLOBAL.EVENTS.STANDINGS.TITLE' | translate">
            @if (standings().length) {
                <!-- Chips rather than sortable column headers. Headers only work in a wide table
                     layout, which would need horizontal scrolling on a phone and a second sort
                     mechanism besides -- and two mechanisms can disagree. One control, identical
                     at every width, and each chip is a real button so it is reachable by keyboard
                     and announced by a screen reader. -->
                <div class="standings-sort">
                    <span class="standings-sort-label">{{ 'GLOBAL.EVENTS.STANDINGS.SORT' | translate }}</span>
                    <div class="standings-sort-options">
                        @for (option of sortOptions(); track option.key) {
                            <ion-button
                                class="btn-xs"
                                [fill]="option.key === sortKey() ? 'solid' : 'clear'"
                                (click)="chooseSort(option.key)"
                            >
                                {{ option.label }}
                                @if (option.key === sortKey()) {
                                    <ion-icon
                                        [name]="sortDirection() === 'asc' ? 'caret-up-outline' : 'caret-down-outline'"
                                        slot="end"
                                    ></ion-icon>
                                }
                            </ion-button>
                        }
                    </div>
                </div>
            }

            <ion-list>
                @for (row of rows(); track row.displayName) {
                    <!-- Everything sits in ion-item's single unslotted area, not its label/end
                         slots: the slots made the rank a full flex child, which is what gave a
                         two-character number a third of the row. -->
                    <ion-item lines="full">
                        <div class="standings-row">
                            <div class="standings-rank">#{{ row.rank }}</div>
                            <div class="standings-detail">
                                <div class="standings-name">{{ row.displayName }}</div>
                                <div class="standings-metrics">
                                    @for (metric of row.metrics; track metric.key) {
                                        <span class="standings-metric" [class.standings-metric--active]="metric.key === sortKey()">
                                            @if (metric.label) {
                                                <span class="standings-metric-label">{{ metric.label }}</span>
                                            }
                                            <span class="standings-metric-value">{{ metric.value }}</span>
                                        </span>
                                    }
                                </div>
                            </div>
                        </div>
                    </ion-item>
                } @empty {
                    <ion-item>
                        <ion-label>{{ 'GLOBAL.EVENTS.STANDINGS.NONE' | translate }}</ion-label>
                    </ion-item>
                }
            </ion-list>
        </abs-page-layout>
    `
    // Styles live in army-builder-shared/src/lib/global/styles/events.scss, per the
    // workspace rule against CSS in component files.
})
export class StandingsPage implements OnInit {
    private eventService = inject(EventService);
    private route = inject(ActivatedRoute);
    private translateService = inject(TranslateService);

    standings = signal<EventStanding[]>([]);
    resultFields = signal<EventResultFieldDef[]>([]);

    readonly sortKey = signal<StandingsSortKey>(RANK_SORT);
    readonly sortDirection = signal<SortDirection>('asc');

    readonly sortOptions = computed(() =>
        standingsSortOptions(this.resultFields(), {
            rank: this.translateService.instant('GLOBAL.EVENTS.STANDINGS.RANK'),
            record: this.translateService.instant('GLOBAL.EVENTS.STANDINGS.RECORD')
        })
    );

    /**
     * Rows ready to render, metrics and all.
     *
     * Built here rather than in the template because the template may not call plain
     * methods (the workspace's Angular performance rule) -- the old version called
     * fieldKey() once per field per row on every change detection pass.
     */
    readonly rows = computed<StandingsRow[]>(() => {
        const fields = this.resultFields();

        return sortStandings(this.standings(), this.sortKey(), this.sortDirection()).map((standing) => ({
            rank: standing.rank,
            displayName: standing.displayName,
            metrics: [
                ...fields.map((field) => ({
                    key: fieldSortKey(field),
                    label: field.label,
                    value: String(standing.resultTotals?.[String(field.id)] ?? 0)
                })),
                // No label: "1-0-0" is self-describing next to its own W-L-D sort chip, and a
                // label here would read as a fourth metric rather than a summary of three.
                { key: RECORD_SORT, label: '', value: `${standing.wins}-${standing.losses}-${standing.draws}` }
            ]
        }));
    });

    ngOnInit() {
        this.route.paramMap
            .pipe(switchMap((params) => this.eventService.getStandings(params.get('eventId')!)))
            .subscribe((standings) => this.standings.set(standings));

        this.route.paramMap
            .pipe(switchMap((params) => this.eventService.getEvent(params.get('eventId')!)))
            .subscribe((event) => this.resultFields.set(event.resultFields ?? []));
    }

    /** Tapping the active column reverses it; tapping another starts it the way round it reads best. */
    chooseSort(key: StandingsSortKey) {
        if (this.sortKey() === key) {
            this.sortDirection.update((current) => (current === 'asc' ? 'desc' : 'asc'));
            return;
        }
        this.sortKey.set(key);
        this.sortDirection.set(this.sortOptions().find((option) => option.key === key)?.defaultDirection ?? 'desc');
    }
}
